import { useQueryClient } from '@tanstack/react-query';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { ReportWidgetChild } from '@ifs-gcp/core/models';

import { useGenerateReport } from '@/queries/generate-report';
import { useUpdateAuditCaseFile } from '@/queries/update-audit-case-file';
import { useAuditStore } from '@/store/auditStore';
import { getBtoa } from '@/utils/getBtoa';
import { getTranslation } from '@/utils/translations';
import { checkUnsavedChanges } from '@/utils/unsavedChanges';

import { PdfViewer } from './pdf-viewer';
import { RenderContainer } from './render-container';
import styles from './report.module.scss';

type ReportProps = {
  element: ReportWidgetChild;
};

export const Report = observer(({ element }: ReportProps) => {
  const { audit_case_uuid: auditCaseUuid } = useParams();
  const {
    i18n: { language },
  } = useTranslation();
  const queryClient = useQueryClient();

  const lastSavedAuditBtoa = useAuditStore((state) => state.lastSavedAuditBtoa);
  const audit = useAuditStore((state) => state.audit)!;

  const domain = useLocalObservable(() => audit.factory('report', element));

  const {
    props: { context, translations },
  } = element;

  useEffect(() => {
    const hasUnsavedChanges = checkUnsavedChanges(lastSavedAuditBtoa, audit);

    if (hasUnsavedChanges) {
      queryClient.removeQueries({
        queryKey: ['report', auditCaseUuid, domain.language, context[0], audit.toIfsFile().hash],
      });
    }
  }, []);

  const {
    refetch: getReport,
    data: reportData,
    isLoading,
    isError,
  } = useGenerateReport({
    auditCaseId: auditCaseUuid!,
    language: domain.language,
    moduleId: context[0],
  });

  const { mutate: updateAuditCaseFile } = useUpdateAuditCaseFile();
  const setLastSavedAuditBtoa = useAuditStore((state) => state.setLastSavedAuditBtoa);

  const onGenerateReport = async () => {
    const hasUnsavedChanges = checkUnsavedChanges(lastSavedAuditBtoa, audit);

    if (hasUnsavedChanges) {
      const saveAuditBtoa = getBtoa(audit.toIfsFile());
      updateAuditCaseFile(
        {
          auditCaseUuid: auditCaseUuid!,
          inputFile: saveAuditBtoa,
        },
        {
          onSuccess: () => {
            setLastSavedAuditBtoa(saveAuditBtoa);
            getReport();
          },
        },
      );
    } else {
      getReport();
    }
  };

  if (!auditCaseUuid) {
    throw new Error('Audit case UUID is required');
  }

  return (
    <div className={styles.container}>
      <h6 className={styles.title}>{getTranslation(translations.title, language)}</h6>
      {!reportData && (
        <div className={styles.renderContainer}>
          <RenderContainer
            isLoading={isLoading}
            isError={isError}
            translations={translations}
            onGenerateReport={onGenerateReport}
          />
        </div>
      )}
      {reportData && <PdfViewer data={reportData} />}
    </div>
  );
});
